import b4a from 'b4a'
import z32 from 'z32'
import { OP, KIND, ROLE, ROOM_TYPE, DELETE_SCOPE, CALL_STATE } from './constants.js'
import { decodeOp } from './ops.js'
import { can, canKick, canSetRole, ACTION, normalizeSettings } from './permissions.js'

/**
 * A stamp past this is not a clock that runs fast but one that is broken
 * (or a hand-made op): 1 January 2100.
 */
const TS_CEILING = 4102444800000

const TS_WIDTH = 14

export function tsKey (ts) {
  return String(ts).padStart(TS_WIDTH, '0')
}

export const KEY = {
  meta: () => 'meta',
  clock: () => 'clock',
  message: (ts, id) => 'm/' + tsKey(ts) + '/' + id,
  messageIndex: (id) => 'mi/' + id,
  thread: (root, ts, id) => 'th/' + root + '/' + tsKey(ts) + '/' + id,
  member: (account) => 'u/' + account,
  // One entry per (message, account, emoji): a person may react with several
  // different emoji, the way Telegram, Slack and Discord allow.
  reaction: (msg, account, emoji) => 'rx/' + msg + '/' + account + '/' + emoji,
  reactionPrefix: (msg, account) => 'rx/' + msg + '/' + account + '/',
  read: (account) => 'rd/' + account,
  pin: (ts, msg) => 'pin/' + tsKey(ts) + '/' + msg,
  pinIndex: (msg) => 'pi/' + msg,
  vote: (poll, account) => 'pv/' + poll + '/' + account,
  call: (ts, id) => 'call/' + tsKey(ts) + '/' + id,
  writer: (key) => 'w/' + key
}

const id32 = (buf) => z32.encode(buf)

/**
 * The Autobase apply handler.
 *
 * Must be a pure function of the linearised op sequence: Autobase replays it
 * from scratch whenever the causal order changes, so nothing here may read the
 * wall clock, generate randomness, or depend on which peer is running it.
 */
export function createApply ({ onSystemMessage = null } = {}) {
  return async function apply (nodes, view, base) {
    const batch = view.batch({ update: false })

    try {
      for (const node of nodes) {
        const op = decodeOp(node.value)
        if (!op) continue // unsigned, malformed or unknown — drop silently
        await applyOp(op, batch, base, { onSystemMessage })
      }
      await batch.flush()
    } catch (err) {
      await batch.close().catch(() => {})
      throw err
    }
  }
}

async function readJSON (batch, key, fallback = null) {
  const node = await batch.get(key)
  return node ? node.value : fallback
}

async function loadMeta (batch) {
  return (await readJSON(batch, KEY.meta())) || null
}

async function loadMember (batch, account) {
  return await readJSON(batch, KEY.member(account))
}

/**
 * The time a message is filed and shown under: the time its sender gave it.
 *
 * This used to be held to "no more than five minutes after the newest message
 * in the room", to stop one fast or hostile clock pinning its messages below
 * everybody else's. But apply cannot read a clock, so it cannot tell a clock
 * an hour fast from a chat that was quiet for an hour — and it treated every
 * quiet chat as the first. A message written after a day of silence was
 * filed five minutes after the last one, and shown so: "the time of the old
 * message plus five minutes". A bot, which ignores anything older than a day
 * as history, never saw it at all. Each message after it moved the room on by
 * another five minutes at most, so a chat that had been quiet for a while
 * never caught up.
 *
 * Senders now stamp with the server's time rather than their own (see
 * pine-core/lib/clock.js), which is what keeps honest clocks in step; what is
 * left here is a guard against nonsense. A member who means to pin messages
 * to the bottom of a chat can still do it — they can also simply write a lot,
 * and the answer to both is the same: delete, kick, block.
 *
 * `clock.max` is still kept: it is how a device knows the newest time in the
 * room (RoomView.clock), to keep a quick reply below what it answers.
 */
async function nextTimestamp (batch, proposed) {
  const clock = (await readJSON(batch, KEY.clock())) || { max: 0, bootstrapped: false }
  let ts = Number(proposed)
  if (!Number.isFinite(ts) || ts <= 0 || ts > TS_CEILING) ts = clock.max + 1
  clock.bootstrapped = true
  if (ts > clock.max) clock.max = ts
  await batch.put(KEY.clock(), clock)
  return ts
}

async function applyOp (op, batch, base, ctx) {
  const author = id32(op.author)
  const meta = await loadMeta(batch)
  const member = await loadMember(batch, author)

  switch (op.type) {
    case OP.ROOM_META:
      return applyRoomMeta(op, author, meta, member, batch)
    case OP.MEMBER_ADD:
      return applyMemberAdd(op, author, meta, member, batch, ctx)
    case OP.MEMBER_REMOVE:
      return applyMemberRemove(op, author, meta, member, batch, base, ctx)
    case OP.MEMBER_ROLE:
      return applyMemberRole(op, author, meta, member, batch, ctx)
    case OP.WRITER_ADD:
      return applyWriterAdd(op, author, meta, member, batch, base)
    case OP.WRITER_REMOVE:
      return applyWriterRemove(op, author, meta, member, batch, base)
    case OP.MESSAGE:
      return applyMessage(op, author, meta, member, batch)
    case OP.EDIT:
      return applyEdit(op, author, meta, member, batch)
    case OP.DELETE:
      return applyDelete(op, author, meta, member, batch)
    case OP.REACTION:
      return applyReaction(op, author, meta, member, batch)
    case OP.READ:
      return applyRead(op, author, member, batch)
    case OP.PIN:
      return applyPin(op, author, meta, member, batch)
    case OP.POLL_VOTE:
      return applyVote(op, author, meta, member, batch)
    case OP.CALL_LOG:
      return applyCallLog(op, author, meta, member, batch)
  }
}

/* ---------------- room lifecycle ---------------- */

async function applyRoomMeta (op, author, meta, member, batch) {
  const v = op.value

  // Bootstrap: the very first ROOM_META establishes the room and its owner.
  if (!meta) {
    await batch.put(KEY.meta(), {
      name: v.name,
      description: v.description,
      avatar: v.avatar ? b4a.toString(v.avatar, 'utf8') : null,
      type: v.type,
      settings: normalizeSettings(v.settings),
      createdAt: op.timestamp,
      createdBy: author
    })
    await batch.put(KEY.member(author), {
      account: author,
      name: '',
      role: ROLE.OWNER,
      avatar: null,
      bio: '',
      joinedAt: op.timestamp
    })
    return
  }

  if (!member) return
  if (!can(ACTION.EDIT_META, member.role, meta.type, meta.settings)) return

  await batch.put(KEY.meta(), {
    ...meta,
    name: v.name,
    description: v.description,
    avatar: v.avatar ? b4a.toString(v.avatar, 'utf8') : meta.avatar,
    settings: normalizeSettings(v.settings),
    updatedAt: op.timestamp,
    updatedBy: author
  })
}

async function applyMemberAdd (op, author, meta, member, batch, ctx) {
  const v = op.value
  const target = id32(v.account)
  const existing = await loadMember(batch, target)

  // Self-update of a profile inside the room (display name, avatar).
  if (target === author && existing) {
    await batch.put(KEY.member(target), {
      ...existing,
      name: v.name || existing.name,
      avatar: v.avatar ? b4a.toString(v.avatar, 'utf8') : existing.avatar,
      bio: v.bio || existing.bio
    })
    return
  }

  if (!meta) return
  if (existing) return

  // A joiner may announce themselves once they hold a valid writer slot.
  const selfJoin = target === author
  const hasWriter = !!(await readJSON(batch, KEY.writer(author)))
  if (selfJoin) {
    if (!hasWriter) return
  } else {
    if (!member) return
    if (!can(ACTION.INVITE, member.role, meta.type, meta.settings)) return
  }

  const role = selfJoin
    ? ROLE.MEMBER
    : Math.min(v.role ?? ROLE.MEMBER, member ? member.role - 1 : ROLE.MEMBER)

  await batch.put(KEY.member(target), {
    account: target,
    name: v.name || '',
    role: Math.max(ROLE.RESTRICTED, role),
    avatar: v.avatar ? b4a.toString(v.avatar, 'utf8') : null,
    bio: v.bio || '',
    joinedAt: op.timestamp
  })

  if (normalizeSettings(meta.settings).joinsVisible) {
    await putSystemMessage(batch, op, {
      event: 'join',
      actor: author,
      target,
      name: v.name || ''
    }, ctx)
  }
}

async function applyMemberRemove (op, author, meta, member, batch, base, ctx) {
  if (!meta || !member) return
  const target = id32(op.value.account)
  const existing = await loadMember(batch, target)
  if (!existing) return

  const leaving = target === author
  if (!leaving && !canKick(member.role, existing.role, meta.type, meta.settings)) return

  await batch.del(KEY.member(target))

  const w = await readJSON(batch, KEY.writer(target))
  if (w) {
    await batch.del(KEY.writer(target))
    try { await base.removeWriter(b4a.from(w.key, 'hex')) } catch { /* already gone */ }
  }

  await putSystemMessage(batch, op, {
    event: leaving ? 'leave' : 'kick',
    actor: author,
    target,
    name: existing.name
  }, ctx)
}

async function applyMemberRole (op, author, meta, member, batch, ctx) {
  if (!meta || !member) return
  const target = id32(op.value.account)
  const existing = await loadMember(batch, target)
  if (!existing) return
  if (!canSetRole(member.role, existing.role, op.value.role, meta.type, meta.settings)) return

  await batch.put(KEY.member(target), { ...existing, role: op.value.role })
  await putSystemMessage(batch, op, {
    event: 'role',
    actor: author,
    target,
    name: existing.name,
    role: op.value.role
  }, ctx)
}

/* ---------------- writers ---------------- */

async function applyWriterAdd (op, author, meta, member, batch, base) {
  const v = op.value
  const account = id32(v.account)
  const keyHex = b4a.toString(v.key, 'hex')

  // Bootstrap: the room creator authorises their own writer core.
  if (!meta) {
    await batch.put(KEY.writer(account), { key: keyHex, account })
    try { await base.addWriter(v.key, { indexer: true }) } catch { /* already a writer */ }
    return
  }

  if (!member) return

  /*
   * Whoever may invite somebody in may authorise the core they write from.
   *
   * These were two different ranks — a group lets any member invite, but only
   * an admin authorise — so a person invited by an ordinary member replicated
   * the whole room and could not post a word in it. Nothing said so; the
   * write simply never folded. An invitation that does not carry write access
   * is not an invitation, and the settings that gate inviting (membersCanInvite)
   * already gate this along with it.
   *
   * A channel is unaffected in practice: posting there needs ADMIN regardless,
   * so a writer core without the rank still cannot say anything.
   */
  const mayAdmit = can(ACTION.ADD_WRITER, member.role, meta.type, meta.settings) ||
    can(ACTION.INVITE, member.role, meta.type, meta.settings)
  if (!mayAdmit) return

  /*
   * An account can hold more than one writer core.
   *
   * A writer core is per device — it is the log this machine appends to — so
   * an account signed in on a phone and a laptop needs two. The index used to
   * hold one key per account and skip anything after it, which meant a second
   * device paired successfully, replicated the whole room, and then silently
   * could not write a word: no error anywhere, just a chat that would not
   * send. The same applied to reinstalling on the same phone.
   *
   * `key` stays the most recent one so records written by older builds keep
   * their shape and every reader that only asks "is this account a writer"
   * goes on working.
   */
  const existing = await readJSON(batch, KEY.writer(account))
  const keys = existing
    ? (Array.isArray(existing.keys) ? existing.keys.slice() : [existing.key])
    : []
  if (keys.includes(keyHex)) return

  keys.push(keyHex)
  await batch.put(KEY.writer(account), { key: keyHex, keys, account, addedBy: author })
  try {
    await base.addWriter(v.key, { indexer: meta.type !== ROOM_TYPE.CHANNEL })
  } catch { /* already a writer */ }
}

async function applyWriterRemove (op, author, meta, member, batch, base) {
  if (!meta || !member) return
  const account = id32(op.value.account)
  const existing = await readJSON(batch, KEY.writer(account))
  if (!existing) return

  const self = account === author
  if (!self && !can(ACTION.KICK, member.role, meta.type, meta.settings)) return

  await batch.del(KEY.writer(account))

  // Every device the account writes from, not only the newest: removing one
  // key would leave an older phone still able to post after being kicked.
  const keys = Array.isArray(existing.keys) ? existing.keys : [existing.key]
  for (const key of keys) {
    try { await base.removeWriter(b4a.from(key, 'hex')) } catch { /* already gone */ }
  }
}

/* ---------------- messages ---------------- */

async function applyMessage (op, author, meta, member, batch) {
  if (!meta || !member) return
  if (!can(ACTION.POST, member.role, meta.type, meta.settings)) return

  const v = op.value
  const mid = id32(op.id)
  const ts = await nextTimestamp(batch, op.timestamp)

  const record = {
    id: mid,
    author,
    timestamp: ts,
    kind: v.kind,
    text: v.text,
    replyTo: v.replyTo ? id32(v.replyTo) : null,
    threadRoot: v.threadRoot ? id32(v.threadRoot) : null,
    forwardFrom: v.forwardFrom ? id32(v.forwardFrom) : null,
    forwardName: v.forwardName || '',
    attachments: v.attachments.map(serializeAttachment),
    entities: v.entities,
    ttl: v.ttl || normalizeSettings(meta.settings).disappearingSeconds || 0,
    extra: parseExtra(v.extra),
    editedAt: 0,
    deleted: false
  }

  const key = KEY.message(ts, mid)
  await batch.put(key, record)
  await batch.put(KEY.messageIndex(mid), { key, ts })

  if (record.threadRoot) {
    await batch.put(KEY.thread(record.threadRoot, ts, mid), { key })
  }
}

async function applyEdit (op, author, meta, member, batch) {
  if (!meta || !member) return
  const target = id32(op.value.target)
  const located = await locate(batch, target)
  if (!located) return
  const { key, record } = located
  if (record.author !== author) return
  if (!can(ACTION.EDIT_OWN, member.role, meta.type, meta.settings)) return
  if (record.deleted) return

  await batch.put(key, {
    ...record,
    text: op.value.text,
    entities: op.value.entities,
    editedAt: op.timestamp
  })
}

async function applyDelete (op, author, meta, member, batch) {
  if (!meta || !member) return
  const target = id32(op.value.target)
  const located = await locate(batch, target)
  if (!located) return
  const { key, record } = located

  const own = record.author === author
  const allowed = own
    ? can(ACTION.DELETE_OWN, member.role, meta.type, meta.settings)
    : can(ACTION.DELETE_ANY, member.role, meta.type, meta.settings)
  if (!allowed) return
  if (op.value.scope !== DELETE_SCOPE.EVERYONE) return // per-device hides stay local

  await batch.put(key, {
    id: record.id,
    author: record.author,
    timestamp: record.timestamp,
    kind: record.kind,
    text: '',
    attachments: [],
    entities: [],
    replyTo: record.replyTo,
    threadRoot: record.threadRoot,
    deleted: true,
    deletedBy: author,
    deletedAt: op.timestamp
  })
}

const MAX_REACTIONS_PER_MEMBER = 8

async function applyReaction (op, author, meta, member, batch) {
  if (!meta || !member) return
  if (!can(ACTION.REACT, member.role, meta.type, meta.settings)) return
  const target = id32(op.value.target)
  if (!(await locate(batch, target))) return

  const key = KEY.reaction(target, author, op.value.emoji)

  if (op.value.remove) {
    await batch.del(key)
    return
  }
  if (await batch.get(key)) return

  // Cap how many distinct emoji one member can pile onto one message, so a
  // hostile peer cannot inflate everyone's view with unbounded entries.
  const prefix = KEY.reactionPrefix(target, author)
  let mine = 0
  for await (const _ of batch.createReadStream({ gte: prefix, lt: prefix + '￿' })) {
    if (++mine >= MAX_REACTIONS_PER_MEMBER) return
  }

  await batch.put(key, { account: author, emoji: op.value.emoji, at: op.timestamp })
}

async function applyRead (op, author, member, batch) {
  if (!member) return
  const prev = await readJSON(batch, KEY.read(author))
  if (prev && prev.at >= op.value.at) return
  await batch.put(KEY.read(author), {
    upTo: id32(op.value.upTo),
    at: op.value.at
  })
}

async function applyPin (op, author, meta, member, batch) {
  if (!meta || !member) return
  if (!can(ACTION.PIN, member.role, meta.type, meta.settings)) return
  const target = id32(op.value.target)
  const located = await locate(batch, target)
  if (!located) return

  if (op.value.remove) {
    const idx = await readJSON(batch, KEY.pinIndex(target))
    if (idx) {
      await batch.del(idx.key)
      await batch.del(KEY.pinIndex(target))
    }
    return
  }
  const key = KEY.pin(op.timestamp, target)
  await batch.put(key, { message: target, by: author, at: op.timestamp })
  await batch.put(KEY.pinIndex(target), { key })
}

async function applyVote (op, author, meta, member, batch) {
  if (!meta || !member) return
  const poll = id32(op.value.poll)
  const located = await locate(batch, poll)
  if (!located || located.record.kind !== KIND.POLL) return

  const config = located.record.extra || {}
  if (config.closesAt && op.timestamp > config.closesAt) return

  const options = config.options || []
  const picks = [...new Set(op.value.options)].filter((i) => i >= 0 && i < options.length)
  if (!picks.length) return
  if (!(config.flags & 1) && picks.length > 1) return // single-choice poll

  await batch.put(KEY.vote(poll, author), { options: picks, at: op.timestamp })
}

async function applyCallLog (op, author, meta, member, batch) {
  if (!meta || !member) return
  const v = op.value
  const callId = id32(v.callId)

  /*
   * One call, one line in the chat.
   *
   * Both ends of a call write it down when it finishes, each with the moment
   * its own side connected, so a call talked through used to show twice. The
   * second record now goes into the first — and the one that says more wins:
   * a call with a duration over "missed", "missed" over an empty "ended" from
   * a phone that only ever rang (which is what an older build writes).
   */
  const existing = await readJSON(batch, KEY.messageIndex(callId))
  if (existing) {
    const node = await batch.get(existing.key)
    const before = node && node.value && node.value.extra
    if (before && before.event === 'call' && callWeight(v.state, v.duration) > callWeight(before.state, before.duration)) {
      await batch.put(existing.key, {
        ...node.value,
        extra: { ...before, state: v.state, video: v.video || before.video, duration: v.duration }
      })
      const call = await readJSON(batch, KEY.call(existing.ts, callId))
      if (call) await batch.put(KEY.call(existing.ts, callId), { ...call, state: v.state, duration: v.duration })
    }
    return
  }

  const ts = await nextTimestamp(batch, v.startedAt || op.timestamp)
  await batch.put(KEY.call(ts, callId), {
    id: callId,
    state: v.state,
    video: v.video,
    startedAt: ts,
    duration: v.duration,
    by: author,
    participants: v.participants.map(id32)
  })

  if (v.state === CALL_STATE.ENDED || v.state === CALL_STATE.MISSED || v.state === CALL_STATE.DECLINED) {
    const mid = callId
    const key = KEY.message(ts, mid)
    if (!(await batch.get(key))) {
      await batch.put(key, {
        id: mid,
        author,
        timestamp: ts,
        kind: KIND.SYSTEM,
        text: '',
        attachments: [],
        entities: [],
        extra: {
          event: 'call',
          state: v.state,
          video: v.video,
          duration: v.duration,
          participants: v.participants.map(id32)
        },
        deleted: false
      })
      await batch.put(KEY.messageIndex(mid), { key, ts })
    }
  }
}

/* ---------------- helpers ---------------- */

function callWeight (state, duration) {
  if (duration > 0) return 3
  if (state === CALL_STATE.MISSED || state === CALL_STATE.DECLINED) return 2
  return 1
}

async function locate (batch, messageId) {
  const idx = await readJSON(batch, KEY.messageIndex(messageId))
  if (!idx) return null
  const node = await batch.get(idx.key)
  if (!node) return null
  return { key: idx.key, record: node.value }
}

async function putSystemMessage (batch, op, extra, ctx) {
  const ts = await nextTimestamp(batch, op.timestamp)
  const mid = id32(op.id)
  const key = KEY.message(ts, mid)
  const record = {
    id: mid,
    author: id32(op.author),
    timestamp: ts,
    kind: KIND.SYSTEM,
    text: '',
    attachments: [],
    entities: [],
    extra,
    deleted: false
  }
  await batch.put(key, record)
  await batch.put(KEY.messageIndex(mid), { key, ts })
  if (ctx && ctx.onSystemMessage) ctx.onSystemMessage(record)
}

function serializeAttachment (a) {
  return {
    blobId: a.blobId ? b4a.toString(a.blobId, 'base64') : null,
    core: a.core ? b4a.toString(a.core, 'hex') : null,
    name: a.name,
    mime: a.mime,
    size: a.size,
    width: a.width,
    height: a.height,
    duration: a.duration,
    thumb: a.thumb ? b4a.toString(a.thumb, 'base64') : null,
    key: a.key ? b4a.toString(a.key, 'hex') : null,
    blurhash: a.blurhash,
    waveform: a.waveform ? Array.from(a.waveform) : null
  }
}

function parseExtra (extra) {
  if (!extra) return null
  try { return JSON.parse(extra) } catch { return null }
}
